/**
 * UploadEpisodes.jsx — June 2 2026.
 *
 * Pill 3 of the upload hub. Writer picks one of their series, sees the
 * hard-cast next episode number ("Episode N"), then submits either:
 *   • an audio file (mp3/wav/m4a/aac/ogg/flac, 5-22 min), OR
 *   • a text body (1000-5000 words) — saved + included in the Saturday
 *     digest email to TheAntagonistari@gmail.com.
 *
 * After a successful submit the form resets and N auto-increments.
 *
 * Edit limit: scribes get 2 edits per episode after the original upload.
 * Admin (`role === 'admin'`) is unlimited. Out-of-edits triggers a
 * `Message admin` button that opens the in-app messages thread.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Headphones, ListPlus, Mic, FileText, Loader2, Plus,
  Pencil, Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { authFetch, checkAuth } from '../utils/authUtils';
import OriginalContentAck from '../components/OriginalContentAck';
import { recordOriginalAck } from '../utils/originalAck';
import { toast } from 'sonner';
import VoiceSelectionModal from '../components/VoiceSelectionModal';
import SynthesisProgressLine from '../components/SynthesisProgressLine';
import { validateAudioDuration } from '../utils/probeAudioDuration';
import { pollChapterSynthesis } from '../utils/pollChapterSynthesis';
import EditQuotaPill from '../components/EditQuotaPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TEXT_MIN = 1000;
const TEXT_MAX = 6000;
const LEVELS = [
  { id: 'basic', label: 'Basic' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'advanced', label: 'Advanced' },
];

const UploadEpisodes = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [seriesId, setSeriesId] = useState(null);
  const [chapters, setChapters] = useState([]);

  const refresh = async (uid, role) => {
    const r = await authFetch(`${BACKEND_URL}/api/library`);
    const d = r.ok ? await r.json() : { series: [] };
    // Feb 12 2026 — High King and Kings see every scribe's series so
    // they can step in and edit on the scribe's behalf. Writers still
    // see only their own series.
    const all = d.series || [];
    if (role === 'admin' || role === 'coadmin' || role === 'king') return all;
    return all.filter((s) => s.user_id === uid);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      if (u.role !== 'writer' && u.role !== 'admin' && u.role !== 'coadmin' && u.role !== 'king') {
        navigate('/upload');
        return;
      }
      if (cancelled) return;
      setMe(u);
      const list = await refresh(u.user_id, u.role);
      if (cancelled) return;
      setSeries(list);
      // Auto-select if `?series_id=...` was supplied (e.g. user just hit
      // "Create series" on /upload-audio-series and we deep-linked here
      // to start at Episode 1). Falls back to the picker if the id no
      // longer belongs to this user.
      const requestedId = searchParams.get('series_id');
      if (requestedId && list.some((s) => s.series_id === requestedId)) {
        setSeriesId(requestedId);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // When a series is picked, fetch its chapter list so we can show the
  // existing episodes + hard-cast the next number.
  useEffect(() => {
    if (!seriesId) { setChapters([]); return; }
    let cancelled = false;
    (async () => {
      const r = await authFetch(`${BACKEND_URL}/api/audiobook/series/${seriesId}/chapters`);
      const d = r.ok ? await r.json() : {};
      if (!cancelled) setChapters(d.chapters || []);
    })();
    return () => { cancelled = true; };
  }, [seriesId]);

  const nextEpisodeNumber = useMemo(() => {
    if (!chapters.length) return 1;
    return Math.max(...chapters.map((c) => Number(c.chapter_number || 0))) + 1;
  }, [chapters]);

  const refreshChapters = async () => {
    const r = await authFetch(`${BACKEND_URL}/api/audiobook/series/${seriesId}/chapters`);
    const d = r.ok ? await r.json() : {};
    setChapters(d.chapters || []);
  };

  if (loading || !me) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-slate-400 text-sm inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </p>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="upload-episodes-page">
      <div className="flex items-center justify-between mb-5">
        <button
          type="button"
          onClick={() => {
            if (seriesId) {
              setSeriesId(null);
              if (searchParams.get('series_id')) {
                setSearchParams({}, { replace: true });
              }
            } else {
              navigate('/upload');
            }
          }}
          className="text-slate-400 hover:text-white inline-flex items-center gap-1 text-sm"
          data-testid="upload-episodes-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <ListPlus className="w-4 h-4 text-emerald-300" />
          <h1 className="text-lg font-bold text-white">Upload Episodes</h1>
        </div>
        <span className="w-12" />
      </div>

      {!seriesId ? (
        <SeriesPicker series={series} onPick={setSeriesId} />
      ) : (
        <EpisodeWorkspace
          me={me}
          series={series.find((s) => s.series_id === seriesId)}
          chapters={chapters}
          nextEpisodeNumber={nextEpisodeNumber}
          onChange={refreshChapters}
        />
      )}
    </main>
  );
};

// ── Series picker ────────────────────────────────────────────────
const SeriesPicker = ({ series, onPick }) => {
  if (series.length === 0) {
    return (
      <div className="text-center py-12">
        <Headphones className="w-10 h-10 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-400 mb-3">No audio series yet.</p>
        <p className="text-xs text-slate-500">Create one in <span className="text-fuchsia-300">Audio Series</span> first.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="upload-episodes-series-picker">
      <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Pick a series</p>
      {series.map((s) => (
        <button
          key={s.series_id}
          type="button"
          onClick={() => onPick(s.series_id)}
          className="w-full text-left flex items-center gap-3 p-3.5 rounded-xl bg-slate-800/60 border border-slate-700 hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40"
          data-testid={`upload-episodes-series-${s.series_id}`}
        >
          <div className="w-10 h-10 rounded-lg bg-slate-900/60 border border-slate-700 flex items-center justify-center flex-shrink-0">
            <Headphones className="w-5 h-5 text-slate-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm truncate">{s.title}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {s.chapter_count || s.episode_count || (s.episodes?.length) || 0} episodes
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};

// ── Episode workspace ───────────────────────────────────────────
const EpisodeWorkspace = ({ me, series, chapters, nextEpisodeNumber, onChange }) => {
  // Feb 12 2026 — High King (admin) + Kings (coadmin / king) all get
  // unlimited edits, mirroring the backend edit_quota.bypasses_quota.
  const isAdmin = ['admin', 'coadmin', 'king'].includes(me?.role);

  return (
    <div className="space-y-5" data-testid="upload-episodes-workspace">
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-100 text-sm">
        <span className="font-bold">{series?.title}</span>
        <span className="text-emerald-200/80"> · next up: <span className="font-black">Episode {nextEpisodeNumber}</span></span>
      </div>

      {/* New episode form */}
      <NewEpisodeForm
        seriesId={series?.series_id}
        episodeNumber={nextEpisodeNumber}
        onSaved={onChange}
      />

      {/* Existing episodes with edit affordance + count */}
      <div className="space-y-2" data-testid="upload-episodes-existing">
        <h3 className="text-sm font-bold text-slate-300 mt-2">Existing episodes</h3>
        {chapters.length === 0 && (
          <p className="text-xs text-slate-500 italic">None yet.</p>
        )}
        {chapters.map((ch) => (
          <EpisodeRow key={ch.chapter_id} chapter={ch} isAdmin={isAdmin} onChange={onChange} />
        ))}
      </div>
    </div>
  );
};

const NewEpisodeForm = ({ seriesId, episodeNumber, onSaved }) => {
  const [title, setTitle] = useState('');
  const [readingLevel, setReadingLevel] = useState('');
  const [bodyMode, setBodyMode] = useState('text'); // 'text' | 'audio'
  const [chapterText, setChapterText] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Per-episode originality acknowledgement. Reset to false after each
  // successful save so the next episode has to re-confirm.
  const [originalAck, setOriginalAck] = useState(false);
  // ── Voice lock (Feb 6 2026 user spec) ──────────────────────────────
  // We fetch the series's `tts_voice` on mount. If set, every text
  // episode is forced to that voice (server-enforced; the UI surfaces
  // it as a locked pill). If unset, the Publish button opens a voice
  // selection modal before submitting — the chosen voice both narrates
  // THIS episode and gets persisted onto the series.
  const [lockedVoice, setLockedVoice] = useState(null);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  // Live progress for background-task synthesis. Drives an inline
  // status line under the Publish button (poll number + elapsed time).
  const [synthProgress, setSynthProgress] = useState(null);

  useEffect(() => {
    if (!seriesId) { setLockedVoice(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/audiobook/series/${seriesId}`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setLockedVoice(d?.tts_voice || null);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [seriesId]);

  // Hard-cast prefix — writer only types what comes after.
  const headline = `Episode ${episodeNumber}`;
  const titleValue = title ? `${headline} — ${title.trim()}` : headline;

  const wordCount = useMemo(() => {
    const t = chapterText.trim();
    return t ? t.split(/\s+/).filter(Boolean).length : 0;
  }, [chapterText]);
  const wcColor = wordCount === 0
    ? 'text-slate-500'
    : wordCount < TEXT_MIN
    ? 'text-amber-400'
    : wordCount > TEXT_MAX
    ? 'text-rose-400'
    : 'text-emerald-400';

  const canSubmit = (() => {
    if (!readingLevel) return false;
    if (!originalAck) return false;
    if (bodyMode === 'text') return wordCount >= TEXT_MIN && wordCount <= TEXT_MAX;
    return !!audioFile;
  })();

  const submit = async (chosenVoice = null) => {
    if (!canSubmit) {
      toast.error('Pick a reading level, confirm originality, and fill text OR audio.');
      return;
    }
    // Text + no series voice yet + caller hasn't supplied one → open
    // the voice picker. The modal calls submit(voice) on confirm.
    if (bodyMode === 'text' && !lockedVoice && !chosenVoice) {
      setVoiceModalOpen(true);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('chapter_number', String(episodeNumber));
      fd.append('chapter_title', titleValue);
      fd.append('reading_level', readingLevel);
      if (bodyMode === 'text') {
        fd.append('chapter_text', chapterText.trim());
        // Server enforces the lock, but we forward the voice anyway so
        // a brand-new series gets recorded correctly on the first hit.
        fd.append('tts_voice', lockedVoice || chosenVoice || 'default');
      } else if (audioFile) {
        fd.append('file', audioFile);
      }
      const r = await authFetch(`${BACKEND_URL}/api/audiobook/series/${seriesId}/chapter`, {
        method: 'POST',
        body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Upload failed.');
        return;
      }
      // First-time lock — surface the chosen voice immediately so the
      // next episode UI shows the locked-voice pill without a refetch.
      if (bodyMode === 'text' && !lockedVoice && chosenVoice) {
        setLockedVoice(chosenVoice);
      }

      // Background-task synthesis (Feb 7 2026): if the chapter came
      // back `synthesis_status: "pending"`, poll until audio lands.
      // The upload completes regardless — we just want to give the
      // writer a confirmation toast when the audio is ready.
      const newChapterId = d?.chapter_id || d?.episode_id || d?.book_id;
      if (d?.synthesis_status === 'pending' && newChapterId) {
        toast.success(`${headline} submitted — narrating audio…`);
        setSynthProgress({ elapsedMs: 0, attempt: 0, status: 'pending' });
        pollChapterSynthesis({
          kind: 'audiobook',
          id: newChapterId,
          onProgress: setSynthProgress,
        })
          .then(() => {
            toast.success(`${headline} audio is live.`);
            setSynthProgress(null);
          })
          .catch((err) => {
            toast.error(`Audio synthesis failed: ${err.message || err}`);
            setSynthProgress(null);
          });
      } else {
        toast.success(`${headline} published.`);
      }
      if (newChapterId) {
        recordOriginalAck(newChapterId).catch(() => {});
      }
      setTitle('');
      setReadingLevel('');
      setBodyMode('text');
      setChapterText('');
      setAudioFile(null);
      setOriginalAck(false);
      setVoiceModalOpen(false);
      onSaved?.();
    } catch {
      toast.error('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 space-y-4" data-testid="new-episode-form">
      <p className="text-xs uppercase tracking-widest font-bold text-emerald-300">New episode</p>

      {/* Hard-cast title */}
      <div>
        <label className="text-sm font-medium text-slate-200 mb-1 block">Episode title</label>
        <div className="flex items-stretch rounded-md overflow-hidden border border-slate-700">
          <span
            className="bg-emerald-600/30 text-emerald-100 font-black text-sm px-3 flex items-center border-r border-slate-700 whitespace-nowrap"
            data-testid="episode-number-cast"
          >
            {headline}
          </span>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="— subtitle (optional)"
            className="bg-slate-950 border-0 text-white rounded-none"
            data-testid="episode-title-input"
          />
        </div>
        <p className="text-[11px] text-slate-500 mt-1">
          The episode number is auto-locked. Subtitle is optional.
        </p>
      </div>

      {/* Reading level */}
      <div>
        <label className="text-sm font-medium text-slate-200 mb-1 block">
          Reading level <span className="text-xs text-rose-400">required</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          {LEVELS.map((l) => {
            const active = readingLevel === l.id;
            return (
              <button
                key={l.id}
                type="button"
                onClick={() => setReadingLevel(active ? '' : l.id)}
                className={`rounded-lg border px-3 py-2 text-sm font-bold ${
                  active
                    ? 'border-emerald-400 bg-emerald-500/15 text-emerald-100'
                    : 'border-slate-700 bg-slate-950/60 text-slate-300'
                }`}
                data-testid={`level-${l.id}`}
              >
                {l.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body mode toggle — XOR */}
      <div>
        <label className="text-sm font-medium text-slate-200 mb-2 block">
          How will this episode be narrated?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3" data-testid="body-mode-cards">
          <button
            type="button"
            onClick={() => { setBodyMode('text'); setAudioFile(null); }}
            data-testid="body-mode-text"
            className={`group rounded-xl border p-3 text-left transition ${
              bodyMode === 'text'
                ? 'border-fuchsia-400 bg-gradient-to-br from-fuchsia-500/15 to-purple-500/10 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]'
                : 'border-slate-700 bg-slate-950/60 hover:border-fuchsia-400/60'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Mic className={`w-4 h-4 ${bodyMode === 'text' ? 'text-fuchsia-300' : 'text-slate-400'}`} />
              <span className="font-bold text-sm text-white">Narrate with my TTS voice</span>
            </div>
            <p className="text-[11px] text-slate-400 leading-snug">
              Paste your script (1,000–6,000 words). We&apos;ll convert it
              to audio with your locked voice and upload it for you.
            </p>
          </button>
          <button
            type="button"
            onClick={() => { setBodyMode('audio'); setChapterText(''); }}
            data-testid="body-mode-audio"
            className={`group rounded-xl border p-3 text-left transition ${
              bodyMode === 'audio'
                ? 'border-emerald-400 bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 shadow-[0_0_0_1px_rgba(52,211,153,0.4)]'
                : 'border-slate-700 bg-slate-950/60 hover:border-emerald-400/60'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <FileText className={`w-4 h-4 ${bodyMode === 'audio' ? 'text-emerald-300' : 'text-slate-400'}`} />
              <span className="font-bold text-sm text-white">Upload my own audio file</span>
            </div>
            <p className="text-[11px] text-slate-400 leading-snug">
              Drop an MP3 or WAV recording — we&apos;ll publish it as-is.
              No voice lock applies to audio uploads.
            </p>
          </button>
        </div>
        {bodyMode === 'text' ? (
          <>
            <Textarea
              value={chapterText}
              onChange={(e) => setChapterText(e.target.value)}
              rows={12}
              placeholder="Type your story here…"
              className="bg-slate-950 border-slate-700 text-white font-mono text-sm"
              data-testid="episode-text-input"
            />
            <p className={`mt-2 text-xs ${wcColor}`} data-testid="word-count">
              {wordCount} words · need {TEXT_MIN}–{TEXT_MAX}
            </p>
          </>
        ) : (
          <label
            className="flex flex-col items-center justify-center w-full p-6 rounded-xl border-2 border-dashed border-slate-700 hover:border-emerald-500 bg-slate-950/40 cursor-pointer"
            data-testid="audio-file-dropzone"
          >
            <Mic className="w-6 h-6 text-emerald-300 mb-2" />
            <p className="text-sm text-slate-200">{audioFile ? audioFile.name : 'Click to choose audio file'}</p>
            <p className="text-[11px] text-slate-500 mt-1">MP3 · WAV · M4A · AAC · OGG · FLAC · 5–22 min</p>
            <input
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
              onChange={async (e) => {
                const f = e.target.files?.[0] || null;
                if (!f) {
                  setAudioFile(null);
                  return;
                }
                // Iter 79: probe duration client-side and reject
                // anything outside the 5-22 min window before the
                // upload starts. Server has the same guard.
                const check = await validateAudioDuration(f);
                if (!check.ok) {
                  toast.error(check.message);
                  e.target.value = ''; // clear input so user can pick again
                  return;
                }
                setAudioFile(f);
              }}
              className="hidden"
              data-testid="audio-file-input"
            />
          </label>
        )}
      </div>

      <OriginalContentAck
        checked={originalAck}
        onChange={setOriginalAck}
        testId="upload-episode-original-ack"
      />

      {/* Series voice lock pill — visible only when the series already
          has a locked narration voice. Tells the scribe the voice is
          fixed for every text episode and can't be changed. */}
      {bodyMode === 'text' && lockedVoice && (
        <div
          className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-2 flex items-center gap-2 text-[12px] text-fuchsia-100"
          data-testid="series-voice-locked"
        >
          <Lock className="w-3.5 h-3.5 text-fuchsia-300" />
          <span>
            Narration voice locked for this series:&nbsp;
            <strong className="text-white">{lockedVoice}</strong>
          </span>
        </div>
      )}

      <Button
        onClick={() => submit()}
        disabled={!canSubmit || submitting}
        className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold"
        data-testid="episode-submit-btn"
      >
        {submitting
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</>
          : <><Plus className="w-4 h-4 mr-2" /> Publish {headline}</>}
      </Button>

      <SynthesisProgressLine progress={synthProgress} />

      <VoiceSelectionModal
        open={voiceModalOpen}
        onCancel={() => setVoiceModalOpen(false)}
        onConfirm={(v) => submit(v)}
        busy={submitting}
      />
    </div>
  );
};

// Edit limit row + admin DM CTA.
const EpisodeRow = ({ chapter, isAdmin, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(chapter.title || '');
  // Feb 12 2026 — scribes get 1 edit per episode (was 2 historically),
  // High King + Kings unlimited. EditQuotaPill below renders the
  // "request edit from King" CTA when the scribe is exhausted.
  const edits = Number(chapter.edit_count || 0);
  const blocked = !isAdmin && edits >= 1;

  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      const r = await authFetch(`${BACKEND_URL}/api/audiobook/chapter/${chapter.chapter_id}`, {
        method: 'PUT',
        body: fd,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Edit failed.');
        return;
      }
      toast.success('Episode updated.');
      setEditing(false);
      onChange?.();
    } catch {
      toast.error('Network error.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-3 rounded-xl bg-slate-800/60 border border-slate-700" data-testid={`episode-row-${chapter.chapter_id}`}>
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-md flex items-center justify-center text-xs font-bold bg-emerald-500/20 text-emerald-200 flex-shrink-0">
          {chapter.chapter_number || '?'}
        </span>
        <div className="flex-1 min-w-0">
          {editing ? (
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-slate-950 border-slate-700 text-white text-sm"
              data-testid={`episode-edit-title-${chapter.chapter_id}`}
            />
          ) : (
            <p className="text-sm font-bold text-white truncate">{chapter.title || 'Untitled'}</p>
          )}
          <p className="text-[11px] text-slate-400 flex items-center gap-2 flex-wrap">
            <span>{Math.round((chapter.duration_seconds || 0) / 60)}m</span>
            {/* Feb 12 2026 — Edit allowance pill replaces the legacy
                "edits used: 0/2" string. Pill self-renders either
                "1 edit left" (writer), "Unlimited" (royal), or a
                "Request edit" CTA (exhausted writer). */}
            <EditQuotaPill
              itemKind="audiobook_chapter"
              itemId={chapter.chapter_id}
              testidPrefix={`episode-quota-${chapter.chapter_id}`}
            />
          </p>
        </div>
        {editing ? (
          <Button
            size="sm"
            onClick={save}
            disabled={busy}
            className="bg-emerald-500 hover:bg-emerald-400 text-white"
            data-testid={`episode-save-${chapter.chapter_id}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
          </Button>
        ) : blocked ? (
          // EditQuotaPill above already renders the "Request edit" CTA,
          // so we hide the Edit button when blocked.
          <Lock className="w-4 h-4 text-rose-300/60" data-testid={`episode-locked-${chapter.chapter_id}`} />
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            className="border-slate-700 text-slate-200 hover:bg-slate-800"
            data-testid={`episode-edit-btn-${chapter.chapter_id}`}
          >
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
        )}
      </div>
    </div>
  );
};

export default UploadEpisodes;
